/**
 * Catalog of the `frameworks.key` values that carry the **solution-design / business-scope
 * model** — the entity model the Technical Scope Model was authored as.
 *
 * A framework key is the stable identity of a framework across all of its *versions*
 * (releases); a compiled release id does not identify a framework. Anything written against
 * "this model" — hand-built landing pages, onboarding screens, model-specific copy — must key
 * off these values rather than off a release or a root folder key, because root folder keys
 * (`solution`, `business`) repeat across unrelated frameworks.
 *
 * The catalog exists because these keys live in two places that used to drift: repo-seeded
 * framework config in `packages/convex/convex/lib/frameworkConfig/` and *live* framework rows
 * authored through DF Admin, which the repo never sees. One list, imported by both the backend
 * and the web app, is the only way a rename in one environment cannot silently un-register a
 * screen in the other (LAT-988: the Solution Design landing registered only for
 * `technical-scope-model` + `data-analytics` and therefore never rendered on UAT).
 */
/**
 * Repo-seeded Technical Scope Model. Authored in
 * `packages/convex/convex/lib/frameworkConfig/technicalScopeModel/constants.ts`, which
 * re-exports this constant so the seed and this catalog cannot disagree.
 */
export const TECHNICAL_SCOPE_MODEL_FRAMEWORK_KEY = "technical-scope-model";
/**
 * Live **production** framework, authored through DF Admin rather than the repo's
 * `frameworkConfig` — so it exists only as `frameworks` data and cannot be read from source.
 * Verified 2026-08-25 to match the repo's Technical Scope Model config byte-for-byte, which is
 * why it carries the same model and shares the same hand-built screens.
 */
export const DATA_ANALYTICS_FRAMEWORK_KEY = "data-analytics";
/**
 * Live **UAT** framework: the same model again, re-keyed there when UAT's `data-analytics`
 * framework was renamed/retired. It is a distinct key, not an alias — nothing resolves
 * `data-projects` back to `data-analytics` at read time.
 */
export const DATA_PROJECTS_FRAMEWORK_KEY = "data-projects";
/**
 * Every framework key that carries the solution-design/business-scope model, across repo seed
 * data and the live production and UAT deployments. Register model-specific screens by mapping
 * over this list; never hand-list a subset.
 */
export const SOLUTION_MODEL_FRAMEWORK_KEYS = [
    TECHNICAL_SCOPE_MODEL_FRAMEWORK_KEY,
    DATA_ANALYTICS_FRAMEWORK_KEY,
    DATA_PROJECTS_FRAMEWORK_KEY,
];
/**
 * Whether a framework key (as returned by `api.frameworks.getProjectStructure`) is one of the
 * frameworks carrying the solution-design model. A missing or unknown key answers `false` —
 * callers fail safe to the generic behaviour rather than to another framework's screens.
 */
export function isSolutionModelFrameworkKey(frameworkKey) {
    if (!frameworkKey)
        return false;
    return SOLUTION_MODEL_FRAMEWORK_KEYS.includes(frameworkKey);
}
